import {noop} from '@benchling/js-utils';
import {useContext, useRef} from 'react';

import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import * as logging from 'util/Logging';

import {SmartChipSnapshotContext} from './SmartChipSnapshotContext';
import {SmartChipType} from './SmartChipTypes';
import {maybeGetSmartChipTypeFromId} from './SmartChipTypeUtils';
import {resolveFromSnapshot} from './snapshotAdapters';
import {incrementSnapshotHit, incrementSnapshotMiss} from './snapshotMetrics';

interface SnapshotResolutionResult<T> {
  item: T | undefined;
  isInaccessible: boolean;
  handleClickLink: () => void;
  itemUrl: string | null;
}

/**
 * Resolves chip data from the SmartChipSnapshotContext (populated during PDF export).
 *
 * During export, the entry's chip state is pre-collected into a snapshot map so that
 * chip components can render without hitting the GQL cache (which isn't populated in
 * the headless export environment — see SnapshotContextProvider). In interactive mode
 * the context is null and this hook returns null, signalling the caller to fall through
 * to the normal GQL cache path.
 *
 * Returns null when:
 * - The snapshot context is absent (interactive mode)
 * - The id is null/undefined
 * - The id is not found in the snapshot map
 *
 * The `as T` cast is validated by the typed adapter output (e.g. StorableAdapterOutput)
 * at the adapter definition site.
 */
export function useSnapshotResolution<T>(
  id: string | null | undefined,
  getSmartChipTypeFromId: (id: string) => SmartChipType
): SnapshotResolutionResult<T> | null {
  const snapshotMap = useContext(SmartChipSnapshotContext);
  const {isExportView} = EditorPropertiesContext.useContext();
  const metricFiredRef = useRef(false);

  if (id == null || snapshotMap == null) {
    return null;
  }

  const chipType = maybeGetSmartChipTypeFromId(id) ?? 'unknown';

  if (!snapshotMap.has(id)) {
    if (isExportView && !metricFiredRef.current) {
      metricFiredRef.current = true;
      incrementSnapshotMiss(chipType, 'key_not_found');
      logging.info('[snapshot_export] chip miss', {
        extra: {chip_type: chipType, reason: 'key_not_found', chip_id: id},
      });
    }
    return null;
  }

  const resolved = resolveFromSnapshot(id, snapshotMap, getSmartChipTypeFromId);

  // resolveFromSnapshot returns null only when the key is absent — already guarded above.
  if (resolved == null) {
    return null;
  }

  if (isExportView && !metricFiredRef.current) {
    metricFiredRef.current = true;
    incrementSnapshotHit(chipType);
  }

  // editUrl comes from NoteReference.webURL which the backend pre-computes.
  const adapterItem = resolved.item;
  const editUrl = adapterItem?.editUrl ?? null;

  return {
    // Cast is validated by StorableAdapterOutput type at adapter definition site
    item: resolved.item as T | undefined,
    isInaccessible: resolved.isInaccessible,
    handleClickLink: noop,
    itemUrl: editUrl,
  };
}
